import { useLocation, useNavigate } from "react-router-dom";
import { Settings, Moon, User, LogOut } from "lucide-react";
import { useEffect, useState } from "react";

import { getModeConfig } from "../theme/modeConfig";
import { supabase } from "../../auth/supabaseClient";
import { getModeFromPath } from "../utils/getMode";
import oikosEduLogo from "../../assets/logos/Oikos_Student_Devices_Logo.png";
import classroomLogo from "../../assets/logos/Oikos_EDU_Classroom.png";
import workspaceLogo from "../../assets/logos/workplace_logo.png";
import {
  clearEduTeacherSessionToken,
  clearStudentDeviceSession,
} from "../../platforms/edu/services/studentDeviceService";
import {
  PLATFORM_BRAND_KEYS,
  fetchPlatformBrandAssets,
  getPlatformBrandImage,
} from "../../platforms/admin/services/platformBrandingService";
import ActionToast from "../components/ActionToast";

/* 🔥 ADD THESE */
import SettingsModal from "../settings/SettingsModal";
import { getModeSettings } from "../settings/getModeSettings";
import useResponsive from "../hooks/useResponsive";

/* =========================
   GLOBAL HEADER
========================= */

export default function GlobalHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const hostname = window.location.hostname;
  const [logoutError, setLogoutError] = useState("");

  const [time, setTime] = useState("");
  const [date, setDate] = useState("");
  const [platformBrandAssets, setPlatformBrandAssets] = useState({});

  /* 🔥 ADD STATE */
  const [openSettings, setOpenSettings] = useState(false);

  const mode = getModeFromPath(location.pathname, hostname);
  const isClassroom = location.pathname.startsWith("/classroom");
  const isClassroomTeacher = location.pathname.startsWith("/classroom/teacher");
  const isClassroomStudent = location.pathname.startsWith("/classroom/student");
  const isWorkspaceAdmin = location.pathname.startsWith("/workspace/admin");
  const isEduAdmin = location.pathname.startsWith("/devices/admin");
  const isEduTeacher =
    location.pathname.startsWith("/devices/teacher");
  const isEduManagement = isEduAdmin || isEduTeacher || isWorkspaceAdmin;
  const isTeacherPortal = isEduTeacher || isClassroomTeacher;
  const canOpenSettings = !isTeacherPortal && !isClassroomStudent;

  const modeData = getModeConfig(mode);
  const headerLogo = isWorkspaceAdmin
    ? getPlatformBrandImage(platformBrandAssets, PLATFORM_BRAND_KEYS.workplace, "logo", workspaceLogo)
    : isClassroom
      ? getPlatformBrandImage(platformBrandAssets, PLATFORM_BRAND_KEYS.classroom, "logo", classroomLogo)
      : isEduManagement
        ? getPlatformBrandImage(platformBrandAssets, PLATFORM_BRAND_KEYS.managedDevices, "logo", oikosEduLogo)
        : modeData.logo;
  const headerLogoAlt = isWorkspaceAdmin
    ? "Oikos EDU Workspace"
    : isClassroom
      ? "OikosEDU Classroom"
      : isEduManagement
        ? "Oikos Managed Devices"
        : modeData.label;
  const managementLogoStyle = isWorkspaceAdmin ? styles.workspaceLogoImage : styles.eduLogoImage;
  const managementLogoPhoneStyle = isWorkspaceAdmin ? styles.workspaceLogoImagePhone : styles.eduLogoImagePhone;
  const headerIconStyle = isWorkspaceAdmin ? styles.workspaceIconBtn : {};

  /* =========================
     DISPLAY / TV CHECK
  ========================= */
  const isDisplayMode =
    !isEduManagement &&
    !isClassroom &&
    (mode === "home" ||
      mode === "business" ||
      mode === "edu" ||
      mode === "nightstand" ||
      mode === "tv");

  /* =========================
     CLOCK
  ========================= */
  useEffect(() => {
    let isMounted = true;
    fetchPlatformBrandAssets().then((assets) => {
      if (isMounted) setPlatformBrandAssets(assets);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();

      setTime(
        now.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );

      setDate(
        now.toLocaleDateString([], {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      );
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const classroomCode = isClassroom ? String(pathParts[2] || "").trim() : "";
    const hallPassMode = location.pathname.startsWith("/edu/hallpass") ? String(pathParts[2] || "admin").trim() : "";
    const hallPassCode = hallPassMode ? String(pathParts[3] || "").trim() : "";
    const eduTeacherCode = isEduTeacher
      ? String(pathParts[2] === "login" ? pathParts[3] || "" : pathParts[2] || "").trim()
      : "";

    if (isClassroomTeacher) {
      clearEduTeacherSessionToken();
      navigate(classroomCode ? `/classroom/teacher/${encodeURIComponent(classroomCode)}` : "/classroom/teacher", { replace: true });
      return;
    }

    if (isEduTeacher) {
      clearEduTeacherSessionToken();
      navigate(eduTeacherCode ? `/devices/teacher/${encodeURIComponent(eduTeacherCode)}` : "/devices/teacher", { replace: true });
      return;
    }

    if (isClassroomStudent) {
      clearStudentDeviceSession();
      navigate(classroomCode ? `/classroom/student/${encodeURIComponent(classroomCode)}` : "/classroom/student", { replace: true });
      return;
    }

    try {
      await supabase.auth.signOut();
      if (location.pathname.startsWith("/classroom/admin")) {
        navigate(classroomCode ? `/classroom/admin/${encodeURIComponent(classroomCode)}` : "/classroom/admin", { replace: true });
        return;
      }
      if (hallPassMode) {
        navigate(hallPassCode ? `/edu/hallpass/${hallPassMode}/${encodeURIComponent(hallPassCode)}` : `/edu/hallpass/${hallPassMode}`, { replace: true });
        return;
      }
      if (isEduAdmin) {
        navigate("/devices/admin", { replace: true });
        return;
      }
      navigate("/login", {
        replace: true,
        state: { from: location.pathname },
      });
    } catch (error) {
      console.error("Logout failed", error);
      setLogoutError("Unable to log out right now.");
    }
  };

  function handleSettingsClick() {
    setOpenSettings(true);
  }

  return (
    <>
      <ActionToast message={logoutError} tone="error" onDismiss={() => setLogoutError("")} />
      <div style={{ ...styles.header, ...(isPhone ? styles.headerPhone : {}) }}>

        {/* =========================
            LEFT SIDE (LOGO)
        ========================= */}
        <div style={{ ...styles.left, ...(isPhone ? styles.leftPhone : {}) }}>
          {isClassroom || isEduManagement ? (
            <div style={{ ...styles.eduDeviceLogo, ...(isPhone ? styles.eduDeviceLogoPhone : {}) }}>
              <img
                src={headerLogo}
                alt={headerLogoAlt}
                style={{ ...styles.logo, ...managementLogoStyle, ...(isPhone ? managementLogoPhoneStyle : {}) }}
              />
            </div>
          ) : (
            <img
              src={headerLogo}
              alt={headerLogoAlt}
              style={{ ...styles.logo, ...(isPhone ? styles.logoPhone : {}) }}
            />
          )}
        </div>

        {/* =========================
            RIGHT SIDE (ALL CONTROLS)
        ========================= */}
        <div style={{ ...styles.right, ...(isPhone ? styles.rightPhone : {}) }}>

          {/* DISPLAY / TV ONLY */}
          {isDisplayMode && (
            <>
              <button style={{ ...styles.iconBtn, ...(isPhone ? styles.iconBtnPhone : {}) }}>
                <User size={20} />
              </button>

              <button style={{ ...styles.iconBtn, ...(isPhone ? styles.iconBtnPhone : {}) }}>
                <Moon size={20} />
              </button>
            </>
          )}

          {/* SETTINGS */}
          {canOpenSettings ? (
            <button
              style={{ ...styles.iconBtn, ...headerIconStyle, ...(isPhone ? styles.iconBtnPhone : {}) }}
              onClick={handleSettingsClick}
              aria-label="Settings"
              title="Settings"
            >
              <Settings size={20} />
            </button>
          ) : null}

          <button
            style={{ ...styles.iconBtn, ...headerIconStyle, ...(isPhone ? styles.iconBtnPhone : {}) }}
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
          >
            <LogOut size={20} />
          </button>

          {/* CLOCK (FURTHEST RIGHT) */}
          <div style={{ ...styles.clock, ...(isPhone ? styles.clockPhone : {}) }}>
            <div style={styles.time}>{time}</div>
            <div style={styles.date}>{date}</div>
          </div>

        </div>

      </div>

      {/* 🔥 SETTINGS MODAL (ADDED) */}
      <SettingsModal
        open={openSettings}
        onClose={() => setOpenSettings(false)}
      >
        {getModeSettings(mode)}
      </SettingsModal>
    </>
  );
}

/* =========================
   STYLES
========================= */

const styles = {
  header: {
    position: "fixed",
    top: 12,
    left: 16,
    right: 16,

    height: "54px",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    padding: "0 12px 0 16px",

    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(18px) saturate(1.12)",
    WebkitBackdropFilter: "blur(18px) saturate(1.12)",

    border: "1px solid rgba(255,255,255,0.56)",
    borderRadius: "24px",
    boxShadow: "0 18px 46px rgba(15,23,42,0.14)",

    zIndex: 200,
  },
  headerPhone: {
    borderRadius: 18,
    height: 48,
    left: 8,
    padding: "0 8px 0 10px",
    right: 8,
    top: 8,
  },

  left: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  leftPhone: {
    flex: "1 1 auto",
    overflow: "hidden",
  },

  right: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  rightPhone: {
    flex: "0 0 auto",
    gap: 5,
  },

  iconBtn: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid rgba(var(--color-primary-rgb),0.12)",
    borderRadius: "14px",
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    height: "36px",
    justifyContent: "center",
    padding: 0,
    width: "36px",
  },
  iconBtnPhone: {
    borderRadius: 12,
    height: 34,
    minWidth: 34,
    width: 34,
  },

  clock: {
    display: "flex",
    flexDirection: "column",
    lineHeight: "1",
    marginLeft: "4px",
    minWidth: "68px",
    padding: "7px 10px",
    borderRadius: "16px",
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(15,23,42,0.06)",
  },
  clockPhone: {
    display: "none",
  },

  time: {
    fontSize: "14px",
    fontWeight: "800",
    color: "#111827",
  },

  date: {
    fontSize: "11px",
    color: "#6b7280",
  },

  logo: {
    height: "30px",
    maxWidth: "min(220px, 48vw)",
    objectFit: "contain",
  },
  logoPhone: {
    height: 26,
    maxWidth: "42vw",
  },
  eduDeviceLogo: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    lineHeight: 1,
    minWidth: 0,
  },
  eduDeviceLogoPhone: {
    maxWidth: "52vw",
  },
  eduLogoImage: {
    height: 30,
    maxWidth: "min(245px, 48vw)",
  },
  eduLogoImagePhone: {
    height: 26,
    maxWidth: "52vw",
  },
  workspaceLogoImage: {
    height: 48,
    maxWidth: "min(300px, 52vw)",
    width: "auto",
  },
  workspaceLogoImagePhone: {
    height: 38,
    maxWidth: "50vw",
  },
  workspaceIconBtn: {
    background: "rgba(134,137,120,0.12)",
    border: "1px solid rgba(134,137,120,0.18)",
    color: "#646757",
  },
};
